import React, { useEffect, useMemo, useState } from 'react';
import { api } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { toast } from 'sonner';
import { Shield, Plus, Trash2, Lock, Users as UsersIcon, Sparkles } from 'lucide-react';

/**
 * AdminRolesTab — Roles & Permissions management.
 * Phase 7A Item 6 (RBAC).
 *
 * Backward compatibility: this UI edits the new `roles` collection. The
 * existing `user.role` string field is untouched — this tab simply lets
 * admins add new roles or tweak the permission sets of existing ones.
 */
const CATEGORY_LABEL = {
  panels: 'Panel Access',
  listings: 'Listings',
  reviews: 'Reviews',
  reports: 'Reports',
  users: 'Users',
  verification: 'Verification',
  payments: 'Payments',
  cms: 'CMS',
  emails: 'Emails',
  promotions: 'Promotions',
  referrals: 'Referrals & Points',
  system: 'System',
};

export const AdminRolesTab = () => {
  const [catalog, setCatalog] = useState({ categories: [], total: 0 });
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // role object or {new:true}
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [permsRes, rolesRes] = await Promise.all([
        api.get('/admin/rbac/permissions'),
        api.get('/admin/rbac/roles'),
      ]);
      setCatalog(permsRes.data);
      setRoles(rolesRes.data.roles);
    } catch (e) {
      toast.error('Failed to load RBAC data', { description: e?.response?.data?.detail || e.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, []);

  const totalPerms = catalog.total;

  const startCreate = () => {
    setEditing({
      _new: true,
      key: '',
      label: '',
      description: '',
      permissions: [],
      active: true,
      is_system: false,
    });
  };

  const startEdit = (role) => {
    setEditing({ ...role, permissions: [...(role.permissions || [])] });
  };

  const closeEditor = () => setEditing(null);

  const save = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      if (editing._new) {
        const payload = {
          key: editing.key.trim().toLowerCase(),
          label: editing.label.trim(),
          description: editing.description?.trim() || '',
          permissions: editing.permissions,
        };
        if (!payload.key || !payload.label) {
          toast.error('Key and Label are required');
          setSaving(false);
          return;
        }
        await api.post('/admin/rbac/roles', payload);
        toast.success(`Role "${payload.label}" created`);
      } else {
        const payload = {
          label: editing.label,
          description: editing.description,
        };
        // Admin role: don't send permissions (locked)
        if (editing.key !== 'admin') {
          payload.permissions = editing.permissions;
        }
        // Only allow toggling active for non-system roles
        if (!editing.is_system) {
          payload.active = editing.active;
        }
        await api.patch(`/admin/rbac/roles/${editing.id}`, payload);
        toast.success(`Role "${editing.label}" updated`);
      }
      await loadAll();
      closeEditor();
    } catch (e) {
      toast.error('Save failed', { description: e?.response?.data?.detail || e.message });
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteConfirm) return;
    try {
      const res = await api.delete(`/admin/rbac/roles/${deleteConfirm.id}`);
      toast.success(`Role deleted`, {
        description: res.data.migrated_users
          ? `${res.data.migrated_users} user(s) migrated to "user" role.`
          : 'No users were affected.',
      });
      setDeleteConfirm(null);
      await loadAll();
    } catch (e) {
      toast.error('Delete failed', { description: e?.response?.data?.detail || e.message });
    }
  };

  const togglePerm = (permKey) => {
    if (!editing) return;
    const has = editing.permissions.includes(permKey);
    setEditing({
      ...editing,
      permissions: has
        ? editing.permissions.filter(p => p !== permKey)
        : [...editing.permissions, permKey],
    });
  };

  const selectAllInCategory = (cat, select) => {
    if (!editing) return;
    const keys = cat.permissions.map(p => p.key);
    const set = new Set(editing.permissions);
    keys.forEach(k => select ? set.add(k) : set.delete(k));
    setEditing({ ...editing, permissions: [...set] });
  };

  const isAdminRole = editing?.key === 'admin';
  const isSystemRole = editing?.is_system === true;
  const disabledCheckboxes = isAdminRole; // admin has wildcard, all locked

  if (loading) {
    return <Card className='p-6 text-sm text-muted-foreground' data-testid='admin-rbac-loading'>Loading roles & permissions…</Card>;
  }

  return (
    <div className='space-y-4' data-testid='admin-rbac-tab'>
      <Card className='p-4'>
        <div className='flex items-center justify-between mb-1'>
          <div>
            <div className='text-lg font-semibold flex items-center gap-2'>
              <Shield className='h-5 w-5 text-primary' /> Roles & Permissions
            </div>
            <p className='text-sm text-muted-foreground mt-0.5'>
              Fine-grained access control. System roles cannot be deleted. The
              <span className='font-medium'> admin</span> role has an implicit wildcard that grants every present and future permission.
            </p>
          </div>
          <Button onClick={startCreate} data-testid='admin-rbac-new-role'>
            <Plus className='h-4 w-4 mr-1' /> New role
          </Button>
        </div>
        <div className='text-xs text-muted-foreground mt-2'>
          {totalPerms} permissions across {catalog.categories?.length || 0} categories · {roles.length} roles defined
        </div>
      </Card>

      <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3'>
        {roles.map((role) => (
          <Card key={role.id} className='p-4 flex flex-col gap-3' data-testid={`admin-rbac-role-${role.key}`}>
            <div className='flex items-start justify-between gap-2'>
              <div className='min-w-0'>
                <div className='flex items-center gap-2 flex-wrap'>
                  <span className='font-semibold truncate'>{role.label}</span>
                  {role.is_system && (
                    <Badge variant='secondary' className='text-[10px] gap-1'>
                      <Lock className='h-3 w-3' /> System
                    </Badge>
                  )}
                  {!role.active && <Badge variant='outline' className='text-[10px]'>Inactive</Badge>}
                </div>
                <div className='text-xs text-muted-foreground mt-0.5'>key: <code className='text-[11px] bg-muted px-1 py-0.5 rounded'>{role.key}</code></div>
              </div>
              <div className='flex items-center gap-1.5 text-xs text-muted-foreground shrink-0'>
                <UsersIcon className='h-3.5 w-3.5' /> {role.user_count}
              </div>
            </div>
            {role.description && (
              <p className='text-xs text-muted-foreground line-clamp-2'>{role.description}</p>
            )}
            <div className='flex flex-wrap gap-1'>
              {role.permissions.includes('*') ? (
                <Badge className='gap-1'>
                  <Sparkles className='h-3 w-3' /> All permissions (wildcard)
                </Badge>
              ) : role.permissions.length === 0 ? (
                <span className='text-xs text-muted-foreground italic'>No permissions granted</span>
              ) : (
                <>
                  {role.permissions.slice(0, 5).map(p => (
                    <Badge key={p} variant='outline' className='text-[10px] font-mono'>{p}</Badge>
                  ))}
                  {role.permissions.length > 5 && (
                    // #2: The "+N more" badge is now an interactive Popover
                    // that reveals the remaining permissions on click.
                    <Popover>
                      <PopoverTrigger asChild>
                        <button
                          type='button'
                          className='inline-flex items-center rounded-md border border-input px-1.5 py-0 text-[10px] hover:bg-secondary transition-colors'
                          data-testid={`admin-rbac-more-${role.key}`}
                          aria-label={`Show remaining ${role.permissions.length - 5} permissions for ${role.name || role.key}`}
                        >
                          +{role.permissions.length - 5} more
                        </button>
                      </PopoverTrigger>
                      <PopoverContent align='start' className='w-72 max-h-72 overflow-y-auto'>
                        <div className='text-xs font-medium mb-2'>All permissions</div>
                        <div className='flex flex-wrap gap-1'>
                          {role.permissions.map(p => (
                            <Badge key={p} variant='outline' className='text-[10px] font-mono'>{p}</Badge>
                          ))}
                        </div>
                      </PopoverContent>
                    </Popover>
                  )}
                </>
              )}
            </div>
            <div className='flex gap-2 mt-auto pt-2'>
              <Button size='sm' variant='outline' onClick={() => startEdit(role)} data-testid={`admin-rbac-edit-${role.key}`}>
                Edit
              </Button>
              {!role.is_system && (
                <Button size='sm' variant='outline' className='text-destructive hover:text-destructive' onClick={() => setDeleteConfirm(role)} data-testid={`admin-rbac-delete-${role.key}`}>
                  <Trash2 className='h-3.5 w-3.5 mr-1' /> Delete
                </Button>
              )}
            </div>
          </Card>
        ))}
      </div>

      {/* Editor dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && closeEditor()}>
        <DialogContent className='max-w-3xl max-h-[90vh] overflow-hidden flex flex-col' data-testid='admin-rbac-editor' onInteractOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle>
              {editing?._new ? 'Create new role' : `Edit role: ${editing?.label}`}
            </DialogTitle>
            <DialogDescription>
              {isAdminRole && 'The admin role holds a wildcard permission. Only label and description can be edited.'}
              {!isAdminRole && !editing?._new && isSystemRole && 'System role — permission set can be edited, but the role cannot be deleted or deactivated.'}
              {editing?._new && 'Create a custom role by choosing a unique key and selecting the permissions it should grant.'}
            </DialogDescription>
          </DialogHeader>

          {editing && (
            <div className='space-y-4 overflow-y-auto pr-1'>
              <div className='grid grid-cols-2 gap-3'>
                <div>
                  <Label htmlFor='role-key'>Key</Label>
                  <Input
                    id='role-key'
                    value={editing.key}
                    disabled={!editing._new}
                    onChange={(e) => setEditing({ ...editing, key: e.target.value })}
                    placeholder='e.g. content_editor'
                    data-testid='admin-rbac-editor-key'
                  />
                  <p className='text-[11px] text-muted-foreground mt-1'>Lowercase, unique. Cannot be changed after creation.</p>
                </div>
                <div>
                  <Label htmlFor='role-label'>Label</Label>
                  <Input
                    id='role-label'
                    value={editing.label}
                    onChange={(e) => setEditing({ ...editing, label: e.target.value })}
                    placeholder='e.g. Content Editor'
                    data-testid='admin-rbac-editor-label'
                  />
                </div>
              </div>
              <div>
                <Label htmlFor='role-desc'>Description</Label>
                <Textarea
                  id='role-desc'
                  value={editing.description || ''}
                  onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                  placeholder='What can holders of this role do?'
                  rows={2}
                  data-testid='admin-rbac-editor-desc'
                />
              </div>

              {!isSystemRole && !editing._new && (
                <div className='flex items-center justify-between rounded-md border p-3'>
                  <div>
                    <div className='text-sm font-medium'>Active</div>
                    <div className='text-xs text-muted-foreground'>Inactive roles behave as if they have no permissions.</div>
                  </div>
                  <Switch checked={editing.active} onCheckedChange={(v) => setEditing({ ...editing, active: v })} />
                </div>
              )}

              <div>
                <div className='flex items-center justify-between mb-2'>
                  <Label>Permissions</Label>
                  <div className='text-xs text-muted-foreground'>
                    {isAdminRole
                      ? 'Wildcard grants all permissions.'
                      : `${editing.permissions.length} of ${totalPerms} selected`}
                  </div>
                </div>

                {isAdminRole ? (
                  <Card className='p-4 flex items-center gap-2 bg-muted/40'>
                    <Sparkles className='h-4 w-4 text-primary' />
                    <div className='text-sm'>Admin role includes every permission automatically — including any added in future releases.</div>
                  </Card>
                ) : (
                  <Accordion type='multiple' className='rounded-md border'>
                    {catalog.categories.map((cat) => {
                      const allInCat = cat.permissions.every(p => editing.permissions.includes(p.key));
                      const someInCat = cat.permissions.some(p => editing.permissions.includes(p.key));
                      const selectedCount = cat.permissions.filter(p => editing.permissions.includes(p.key)).length;
                      return (
                        <AccordionItem key={cat.category} value={cat.category} className='border-b last:border-b-0'>
                          <AccordionTrigger className='px-3 hover:no-underline'>
                            <div className='flex items-center justify-between w-full pr-2'>
                              <span className='font-medium text-sm'>{CATEGORY_LABEL[cat.category] || cat.category}</span>
                              <Badge variant={allInCat ? 'default' : someInCat ? 'secondary' : 'outline'} className='text-[10px]'>
                                {selectedCount}/{cat.permissions.length}
                              </Badge>
                            </div>
                          </AccordionTrigger>
                          <AccordionContent className='px-3 pb-3'>
                            <div className='flex gap-2 mb-2'>
                              <Button size='sm' variant='outline' onClick={() => selectAllInCategory(cat, true)} disabled={disabledCheckboxes}>Select all</Button>
                              <Button size='sm' variant='outline' onClick={() => selectAllInCategory(cat, false)} disabled={disabledCheckboxes}>Clear</Button>
                            </div>
                            <div className='grid grid-cols-1 gap-2'>
                              {cat.permissions.map((p) => {
                                const checked = editing.permissions.includes(p.key);
                                return (
                                  <label key={p.key} className='flex items-start gap-3 rounded-md border p-2 cursor-pointer hover:bg-secondary/40 transition-colors'>
                                    <Checkbox
                                      checked={checked}
                                      onCheckedChange={() => togglePerm(p.key)}
                                      disabled={disabledCheckboxes}
                                      data-testid={`admin-rbac-perm-${p.key}`}
                                    />
                                    <div className='min-w-0 flex-1'>
                                      <div className='flex items-center gap-2 flex-wrap'>
                                        <span className='text-sm font-medium'>{p.label}</span>
                                        <code className='text-[10px] bg-muted px-1 py-0.5 rounded font-mono'>{p.key}</code>
                                      </div>
                                      {p.description && <p className='text-xs text-muted-foreground mt-0.5'>{p.description}</p>}
                                    </div>
                                  </label>
                                );
                              })}
                            </div>
                          </AccordionContent>
                        </AccordionItem>
                      );
                    })}
                  </Accordion>
                )}
              </div>
            </div>
          )}

          <DialogFooter className='pt-2 border-t'>
            <Button variant='outline' onClick={closeEditor} disabled={saving}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid='admin-rbac-editor-save'>
              {saving ? 'Saving…' : (editing?._new ? 'Create role' : 'Save changes')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={(o) => !o && setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete role &ldquo;{deleteConfirm?.label}&rdquo;?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteConfirm?.user_count
                ? `${deleteConfirm.user_count} user(s) currently hold this role. They will be migrated to the default "user" role.`
                : 'No users currently hold this role. Deletion is safe.'}
              <br />
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className='bg-destructive text-destructive-foreground hover:bg-destructive/90' data-testid='admin-rbac-delete-confirm'>
              Delete role
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminRolesTab;
